(ns ycweb.app.website.cart.parts.header
  (:require
   [rum.core :as rum]
   [util.string :as str]
   [ycweb.app.website.cart.model :as model]
   [ycweb.app.website.cart.parts.common :as cart.common]
   [ycweb.app.website.common :as web-common]
   [ycweb.components.buttons :as buttons]
   [ycweb.components.svg-icons :as svg-icons]
   [ycweb.components.common :as cmn]
   [ycweb.components.grid :as grid]
   [ycweb.util.core :as u]))

;; --- New Checkout Header Components ---

(rum/defc back-button < rum/reactive cmn/cursors [app-atom process-atom cart-summary-atom]
  "Context-aware back button for checkout steps using step-def navigation"
  (let [step-info (cart.common/current-step-info process-atom)
        current-substep (cart.common/current-substep process-atom)
        terminus-substeps (:terminus-substeps step-info)
        is-terminus? (and current-substep (contains? terminus-substeps current-substep))
        back-info
        (if is-terminus?
          ["/" "Continue shopping"]
          (:back step-info))
        [href text back-action]
        (cond
          ;; If back is a keyword, it's an internal step navigation
          (keyword? back-info)
          [nil "Back" #(cart.common/back-step app-atom process-atom cart-summary-atom %)]

          ;; If back is a vector [href text], it's an external link
          (vector? back-info)
          [(first back-info) (second back-info) nil]

          ;; Default fallback
          :else
          ["/" "Continue shopping" nil])]

    [:div {:class "checkout-header__button-wrapper"}
     [:a {:href href
          :class "checkout-header-button"
          :on-click back-action}
      [:span {:class "checkout-header-button__text"}
       [:i {:class "checkout-header-button__icon icon icon--block icon--arrow-left icon--is-light"}]
       [:span {:class "clip-text unclip-text--m"} text]]]]))

(rum/defc yuppiechef-logo
  "Yuppiechef logo using svg-icon system"
  []
  [:div {:class "checkout-header__logo-wrapper"}
   [:a {:href "/" :class "checkout-header__logo-link"}
    (svg-icons/svg-icon-yuppiechef-logo {:icon-class "checkout-header__logo"})
    [:span {:class "u-hidden-from-view"} "Yuppiechef"]]])

(rum/defc help-button
  "Help section link - opens the need help modal"
  [app-atom]
  [:div {:class "checkout-header__button-wrapper"}
   [:a {:href "/help.htm"
        :class "checkout-header-button"
        :on-click
        (fn [e]
          (.preventDefault e)
          (grid/open-modal app-atom :web.header/need-help-popup
            {:config {:title "Need help?" :max-width 320}}))}
    [:span {:class "checkout-header-button__text"}
     [:i {:class "checkout-header-button__icon icon icon--block icon--help icon--is-light"}]
     [:span {:class "clip-text unclip-text--m"} "Need help?"]]]])

(rum/defc account-button < rum/reactive cmn/cursors [app-atom]
  "Account modal trigger with avatar image, or login link if not logged in"
  (let [user (cmn/curact app-atom [:user])
        user-details {:user/avatar-letter (:avatarletter user)
                      :user/avatar-colour (:avatarcolour user)
                      :user/public-id (:public-id user)}]
    (cond
      user
      ;; Logged in: show avatar + Account, open modal
      [:div {:class "checkout-account-button"}
       [:a {:href "#"
            :class "button button--raised button--default button--secondary button--bordered button--has-icon-left"
            :on-click #(grid/open-modal app-atom :web.header/account-options-popup
                         {:config
                          {:title "Account Options"
                           :max-width 320}})}
        [:span {:class "button__text"}
         [:div {:class "button__icon button__avatar"}
          (web-common/user-avatar user-details {:width 32 :height 32})]
         "Account"]]]

      :else
      ;; Not logged in: show login button
      (buttons/button-web "Log in"
        {:href "/login.htm?afterloginredirect=/order.htm?action=ShowCart"
         :style :secondary
         :size :default
         :icon :user
         :icon-placement :left}))))

(rum/defc checkout-header < rum/reactive cmn/cursors [app-atom process-atom cart-summary-atom]
  "Main header with skip, back, logo, help buttons"
  (let [app-version (cmn/curact app-atom [:app :version])
        dev? (= app-version "dev")]
    [:header {:class (str "checkout-header"
                       (when dev? " checkout-header--is-dev header-wrapper--is-dev"))}
     (cart.common/skip-button "#main-content")
     (back-button app-atom process-atom cart-summary-atom)
     (yuppiechef-logo)
     (help-button app-atom)]))

;; --- New Process Bar Components ---

(rum/defc process-bar-item [step-number current-step title & [options]]

  (let [{:keys [href on-click]} options
        is-active (= step-number current-step)
        is-done (or (< step-number current-step) (and (= step-number 5) is-active))
        is-kinda-done (= step-number current-step)]
    [:li
     {:class
      (str "process-bar-item"
        (when is-active " process-bar-item--is-active")
        (when is-done " process-bar-item--is-done")
        (when is-kinda-done " process-bar-item--is-kinda-done"))}
     [:span {:class "process-bar-item__text"} (str step-number ". " title)]
     [:span {:class "process-bar-item__line"}]]))

(rum/defc process-bar < rum/reactive cmn/cursors [current-step & [has-error?]]
  [:div {:class "yc-section yc-section--standard"}
   [:ol
    {:class (str "process-bar" (when has-error? " process-bar--has-error"))
     :data-steps "5"}
    (process-bar-item 1 current-step "Cart")
    (process-bar-item 2 current-step "Delivery")
    (process-bar-item 3 current-step "Details")
    (process-bar-item 4 current-step "Payment")
    (process-bar-item 5 current-step "Confirm")]])

(rum/defc checkout-step-title < rum/reactive cmn/cursors [app-atom process-atom]
  "Page title with account integration - using step-def for titles"
  (let [step-info (cart.common/current-step-info process-atom)
        title (:title step-info)]
    [:div {:class "yc-section yc-section--standard"}
     [:div {:class "flex-grid-group flex-grid-group--flush-force flex-grid-group--align-left flex-grid-group--valign-middle"}
      [:div {:class "flex-grid-block flex-grid-block--full flex-grid-block--s-fluid flex-grid-block--s-grow-yes"}
       [:div {:class "flex-grid-content"}
        [:h1 {:class "yc-heading yc-heading--large yc-heading--flush"} title]]]
      [:div {:class "flex-grid-block flex-grid-block--full flex-grid-block--s-fluid"}
       [:div {:class "flex-grid-content"}
        (account-button app-atom)]]]]))

;; --- Updated Main Header Component ---

(rum/defc step-header < cmn/cursors
  "Updated stage header using new checkout header structure"
  [app-atom process-atom cart-atom])

(rum/defc step-buttons-view < cmn/cursors rum/reactive
  "Updated stage buttons view using new 5-step process bar"
  [app-atom process-atom]
  (let [step-info (cart.common/current-step-info process-atom)
        step-number (:step step-info)
        has-error? (model/has-checkout-error? app-atom)]
    (process-bar step-number has-error?)))
